import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Building2, Plus, Edit2, Trash2, Mail, Phone, Globe, MapPin, RefreshCw, Zap, Settings, Server, X, Clock, ChevronDown, ChevronRight, Radio, Wifi, PowerOff, Upload, FileText } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function WarrantyPdfManager({ dealershipId, brands }) {
  const { token } = useAuth();
  const [pdfs, setPdfs] = useState([]);
  const [uploading, setUploading] = useState(null);

  useEffect(() => {
    if (!dealershipId) return;
    axios.get(`${API}/warranty-pdfs/${dealershipId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setPdfs(r.data))
      .catch(() => {});
  }, [dealershipId, token]);

  const handleUpload = async (brand, file) => {
    if (!file || !file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Only PDF files are allowed');
      return;
    }
    setUploading(brand);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('brand', brand);
      formData.append('dealership_id', dealershipId);
      const res = await axios.post(`${API}/warranty-pdfs/upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setPdfs(prev => {
        const filtered = prev.filter(p => p.brand !== brand);
        return [...filtered, res.data];
      });
      toast.success(`${brand} warranty PDF uploaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(null);
    }
  };

  const handleDelete = async (fileId, brand) => {
    try {
      await axios.delete(`${API}/warranty-pdfs/${fileId}`, { headers: { Authorization: `Bearer ${token}` } });
      setPdfs(prev => prev.filter(p => p.file_id !== fileId));
      toast.success(`${brand} warranty PDF removed`);
    } catch {
      toast.error('Delete failed');
    }
  };

  const allBrands = brands.length > 0 ? brands : ['Chevrolet', 'GMC', 'Buick', 'Cadillac'];

  return (
    <div className="space-y-2">
      <p className="text-[11px] text-slate-600">Upload a warranty PDF for each brand. Linked from the "New Vehicle Warranty" email button.</p>
      <div className="grid grid-cols-2 gap-2">
        {allBrands.map(brand => {
          const existing = pdfs.find(p => p.brand === brand);
          return (
            <div key={brand} className="flex items-center gap-2 p-2 rounded bg-[#111] border border-[#262626]">
              <FileText className="w-4 h-4 text-slate-500 shrink-0" />
              <span className="text-xs text-slate-300 flex-1">{brand}</span>
              {existing ? (
                <div className="flex items-center gap-1.5">
                  <a href={`${API}/warranty-pdfs/view/${existing.file_id}`} target="_blank" rel="noopener noreferrer"
                     className="text-[10px] text-blue-400 hover:text-blue-300 underline">View</a>
                  <button onClick={() => handleDelete(existing.file_id, brand)}
                          className="text-[10px] text-red-400 hover:text-red-300">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <label className="cursor-pointer flex items-center gap-1 text-[10px] text-slate-400 hover:text-slate-200">
                  <Upload className="w-3 h-3" />
                  {uploading === brand ? 'Uploading...' : 'Upload'}
                  <input type="file" accept=".pdf" className="hidden"
                         disabled={uploading === brand}
                         onChange={e => { if (e.target.files[0]) handleUpload(brand, e.target.files[0]); e.target.value = ''; }}
                         data-testid={`warranty-upload-${brand.toLowerCase()}`} />
                </label>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const CRM_OPTIONS = [
  { value: 'dealersocket', label: 'DealerSocket' },
  { value: 'drivecentric', label: 'DriveCentric' },
  { value: 'elead', label: 'Elead' },
  { value: 'ford_direct', label: 'Ford Direct' },
  { value: 'promax', label: 'ProMax' },
  { value: 'rr', label: 'R&R' },
  { value: 'tekion', label: 'Tekion' },
  { value: 'vinsolutions', label: 'VinSolutions' },
];

const US_STATES = [
  { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' }, { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' }, { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' }, { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' }, { value: 'HI', label: 'Hawaii' }, { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' }, { value: 'IN', label: 'Indiana' }, { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' }, { value: 'KY', label: 'Kentucky' }, { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' }, { value: 'MD', label: 'Maryland' }, { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' }, { value: 'MN', label: 'Minnesota' }, { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' }, { value: 'MT', label: 'Montana' }, { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' }, { value: 'NH', label: 'New Hampshire' }, { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' }, { value: 'NY', label: 'New York' }, { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' }, { value: 'OH', label: 'Ohio' }, { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' }, { value: 'PA', label: 'Pennsylvania' }, { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' }, { value: 'SD', label: 'South Dakota' }, { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' }, { value: 'UT', label: 'Utah' }, { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' }, { value: 'WA', label: 'Washington' }, { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' }, { value: 'WY', label: 'Wyoming' }, { value: 'DC', label: 'District of Columbia' },
];

const capitalizeWords = (str) => str ? str.replace(/\b\w/g, c => c.toUpperCase()) : '';

const BRAND_OPTIONS = [
  'Alfa Romeo', 'Audi', 'BMW', 'Buick', 'Cadillac', 'Chevrolet', 'Chrysler', 'Dodge',
  'FIAT', 'Ford', 'Genesis', 'GMC', 'Honda', 'Hyundai', 'INFINITI', 'Jaguar', 'Jeep',
  'Kia', 'Land Rover', 'Lexus', 'Lincoln', 'Maserati', 'Mazda', 'Mercedes-Benz', 'MINI',
  'Mitsubishi', 'Nissan', 'RAM', 'Subaru', 'Toyota', 'Volkswagen', 'Volvo',
];
const formatPhone = (value) => {
  if (!value) return '';
  const d = value.replace(/\D/g, '');
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0,3)}-${d.slice(3)}`;
  return `${d.slice(0,3)}-${d.slice(3,6)}-${d.slice(6,10)}`;
};
const formatWebsite = (value) => {
  if (!value) return '';
  let c = value.toLowerCase().trim();
  c = c.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, '');
  return c;
};

export default function DealershipsPage() {
  const [dealerships, setDealerships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: '', email_alias: '', phone: '', address: '', website: '', city: '', state: '', zip_code: '', crm: '', gmail_account_id: '', brands: [], brand_websites: {}, reply_to_email: '', test_drive_url: '', preowned_vehicles_url: '', new_vehicles_url: '', preowned_specials_url: '', new_vehicle_specials_url: '', email_aliases: [], hours_url: '', window_sticker_url_template: ''
  });
  const [saving, setSaving] = useState(false);
  const [pollingDealership, setPollingDealership] = useState(null);
  const [scrapingHours, setScrapingHours] = useState(null);
  const [editingHours, setEditingHours] = useState(null);
  const [hoursInput, setHoursInput] = useState('');
  const [expandedCards, setExpandedCards] = useState({});
  const [showSettings, setShowSettings] = useState(null);
  const [gmailSettings, setGmailSettings] = useState({});
  const [gmailAccounts, setGmailAccounts] = useState([]);
  const [aliasesExpanded, setAliasesExpanded] = useState(false);
  const [urlsExpanded, setUrlsExpanded] = useState(false);
  const [dealerInfoExpanded, setDealerInfoExpanded] = useState(false);
  const [editingCoverage, setEditingCoverage] = useState(null);
  const [coverageHours, setCoverageHours] = useState({});
  const [editingBusinessHours, setEditingBusinessHours] = useState(null);
  const [businessHoursData, setBusinessHoursData] = useState({});
  const [collapsedBizHours, setCollapsedBizHours] = useState(() => {
    try { return JSON.parse(localStorage.getItem('collapsedBizHours') || '{}'); } catch { return {}; }
  });
  const [collapsedCovHours, setCollapsedCovHours] = useState(() => {
    try { return JSON.parse(localStorage.getItem('collapsedCovHours') || '{}'); } catch { return {}; }
  });
  const { token } = useAuth();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    fetchDealerships();
    fetchGmailAccounts();
    const gmailConnected = searchParams.get('gmail_connected');
    const error = searchParams.get('error');
    if (gmailConnected === 'true') {
      toast.success('Gmail connected successfully!');
      setSearchParams({});
    } else if (error) {
      const errorMessages = {
        'invalid_state': 'Invalid OAuth state. Please try again.',
        'state_expired': 'OAuth session expired. Please try again.',
        'oauth_failed': 'Failed to connect Gmail. Please try again.'
      };
      toast.error(errorMessages[error] || 'Failed to connect Gmail');
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  const fetchDealerships = async () => {
    try {
      const res = await axios.get(`${API}/dealerships`, { headers, withCredentials: true });
      setDealerships(res.data);
    } catch (err) { console.error('Failed to fetch dealerships:', err); }
    setLoading(false);
  };

  const fetchGmailAccounts = async () => {
    try {
      const res = await axios.get(`${API}/gmail-accounts`, { headers, withCredentials: true });
      setGmailAccounts(res.data);
    } catch (err) { console.error('Failed to fetch Gmail accounts:', err); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        gmail_account_id: form.gmail_account_id || null,
        email_aliases: (form.crm === 'vinsolutions' || form.crm === 'elead') ? (form.email_aliases || []) : undefined
      };
      if (editing) {
        await axios.put(`${API}/dealerships/${editing}`, payload, { headers, withCredentials: true });
        toast.success('Dealership updated');
      } else {
        await axios.post(`${API}/dealerships`, payload, { headers, withCredentials: true });
        toast.success('Dealership added');
      }
      setShowForm(false);
      setEditing(null);
      setForm({ name: '', email_alias: '', phone: '', address: '', website: '', city: '', state: '', zip_code: '', crm: '', gmail_account_id: '', email_aliases: [], hours_url: '' });
      fetchDealerships();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save dealership');
    }
    setSaving(false);
  };

  const handleEdit = (dlr) => {
    setEditing(dlr.dealership_id);
    setForm({
      name: dlr.name || '',
      email_alias: dlr.email_alias || '',
      phone: dlr.phone || '',
      address: dlr.address || '',
      website: dlr.website || '',
      city: dlr.city || '',
      state: dlr.state || '',
      zip_code: dlr.zip_code || '',
      crm: dlr.crm || '',
      gmail_account_id: dlr.gmail_account_id || '',
      brands: dlr.brands || [],
      brand_websites: dlr.brand_websites || {},
      reply_to_email: dlr.reply_to_email || '',
      test_drive_url: dlr.test_drive_url || '',
      preowned_vehicles_url: dlr.preowned_vehicles_url || '',
      new_vehicles_url: dlr.new_vehicles_url || '',
      preowned_specials_url: dlr.preowned_specials_url || '',
      new_vehicle_specials_url: dlr.new_vehicle_specials_url || '',
      email_aliases: dlr.email_aliases || [],
      hours_url: dlr.hours_url || '',
      window_sticker_url_template: dlr.window_sticker_url_template || ''
    });
    setAliasesExpanded((dlr.email_aliases || []).length > 0);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this dealership? This action cannot be undone.')) return;
    try {
      await axios.delete(`${API}/dealerships/${id}`, { headers, withCredentials: true });
      toast.success('Dealership deleted');
      fetchDealerships();
    } catch (err) { console.error('Failed to delete dealership:', err); toast.error('Failed to delete dealership'); }
  };

  const handleDeactivate = async (id, name) => {
    if (!window.confirm(`Deactivate ${name}? It will stop processing leads and move to the Deactivated list.`)) return;
    try {
      await axios.put(`${API}/dealerships/${id}/deactivate`, {}, { headers, withCredentials: true });
      toast.success(`${name} deactivated`);
      fetchDealerships();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to deactivate');
    }
  };

  const openNew = () => {
    setEditing(null);
    setForm({ name: '', email_alias: '', phone: '', address: '', website: '', city: '', state: '', zip_code: '', crm: '', gmail_account_id: '', brands: [], brand_websites: {}, email_aliases: [], hours_url: '' });
    setShowForm(true);
  };

  const pollEmails = async (dealershipId) => {
    setPollingDealership(dealershipId);
    try {
      const res = await axios.post(`${API}/gmail/poll/${dealershipId}`, {}, { headers, withCredentials: true });
      if (res.data.emails_processed > 0) {
        toast.success(`Processed ${res.data.emails_processed} new leads!`);
      } else {
        toast.info('No new emails found');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to poll emails');
    }
    setPollingDealership(null);
  };

  const scrapeBusinessHours = async (dealershipId) => {
    setScrapingHours(dealershipId);
    try {
      const res = await axios.post(`${API}/dealerships/${dealershipId}/scrape-hours`, {}, { headers, withCredentials: true });
      if (res.data.business_hours) {
        toast.success('Business hours extracted successfully');
      } else {
        toast.info(res.data.message || 'Could not find business hours on the website');
      }
      fetchDealerships();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to scrape business hours');
    }
    setScrapingHours(null);
  };

  const saveBusinessHours = async (dealershipId) => {
    try {
      await axios.put(`${API}/dealerships/${dealershipId}/business-hours`, { business_hours: hoursInput }, { headers, withCredentials: true });
      toast.success('Business hours updated');
      setEditingHours(null);
      setHoursInput('');
      fetchDealerships();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save business hours');
    }
  };

  const DAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const DAY_LABELS = { sunday: 'Sun', monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat' };
  const HOURS = Array.from({ length: 24 }, (_, i) => {
    const h = i % 12 || 12;
    const ampm = i < 12 ? 'AM' : 'PM';
    return { value: `${i}:00`, label: `${h}:00 ${ampm}` };
  });

  const initCoverageHours = (dlr) => {
    const existing = dlr.coverage_hours || {};
    const hours = {};
    DAYS.forEach(d => {
      hours[d] = existing[d] || { enabled: true, all_day: true, start: '8:00', end: '20:00' };
    });
    setCoverageHours(hours);
    setEditingCoverage(dlr.dealership_id);
  };

  const saveCoverageHours = async (dealershipId) => {
    try {
      await axios.put(`${API}/dealerships/${dealershipId}`, { coverage_hours: coverageHours }, { headers, withCredentials: true });
      toast.success('Coverage hours saved');
      setEditingCoverage(null);
      fetchDealerships();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save coverage hours');
    }
  };

  const initBusinessHours = (dlr) => {
    const existing = dlr.business_hours_structured || {};
    const hours = {};
    DAYS.forEach(d => {
      hours[d] = existing[d] || { enabled: true, all_day: false, start: '9:00', end: '19:00' };
    });
    setBusinessHoursData(hours);
    setEditingBusinessHours(dlr.dealership_id);
  };

  const saveBusinessHoursStructured = async (dealershipId) => {
    try {
      // Build display string from structured data
      const display = DAYS.filter(d => businessHoursData[d]?.enabled).map(d => {
        const h = businessHoursData[d];
        const label = DAY_LABELS[d];
        if (h.all_day) return `${label}: 24 Hours`;
        const fmt = (t) => { const [hr, mn] = t.split(':'); const h12 = parseInt(hr) % 12 || 12; const ampm = parseInt(hr) < 12 ? 'AM' : 'PM'; return mn === '00' ? `${h12}${ampm}` : `${h12}:${mn}${ampm}`; };
        return `${label}: ${fmt(h.start)}-${fmt(h.end)}`;
      }).join('; ');
      const closed = DAYS.filter(d => !businessHoursData[d]?.enabled).map(d => DAY_LABELS[d]);
      const fullDisplay = closed.length > 0 ? `${display}; ${closed.join(', ')}: Closed` : display;

      await axios.put(`${API}/dealerships/${dealershipId}`, {
        business_hours: fullDisplay,
        business_hours_structured: businessHoursData
      }, { headers, withCredentials: true });
      toast.success('Business hours saved');
      setEditingBusinessHours(null);
      fetchDealerships();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save business hours');
    }
  };

  const openSettings = (dlr) => {
    setShowSettings(dlr.dealership_id);
    setGmailSettings({
      auto_respond: dlr.auto_respond || false,
      polling_enabled: dlr.polling_enabled !== false,
      sms_enabled: dlr.sms_enabled || false,
      respond_to_bad_leads: dlr.respond_to_bad_leads || false,
      polling_interval_seconds: dlr.polling_interval_seconds || 60,
      lead_notify_enabled: dlr.lead_notify_enabled || false,
      lead_notify_phone: dlr.lead_notify_phone || '',
      lead_notify_format: dlr.lead_notify_format || 'formatted',
      scheduling_enabled: dlr.scheduling_enabled || false,
      scheduling_preferred_channel: dlr.scheduling_preferred_channel || 'email',
      utm_campaign: dlr.utm_campaign || '',
      twilio_phone_number: dlr.twilio_phone_number || '',
    });
  };

  const saveSettings = async (dealershipId) => {
    try {
      await axios.put(`${API}/gmail/settings/${dealershipId}`, gmailSettings, { headers, withCredentials: true });
      toast.success('Settings saved');
      setShowSettings(null);
      fetchDealerships();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    }
  };

  // Helper to get the account info for a dealership
  const getAccountForDealership = (dlr) => {
    if (!dlr.gmail_account_id) return null;
    return gmailAccounts.find(a => a.account_id === dlr.gmail_account_id);
  };

  const connectedAccounts = gmailAccounts.filter(a => a.is_connected);

  const sortedDealerships = useMemo(() =>
    [...dealerships].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [dealerships]
  );

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="dealerships-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white uppercase tracking-tight">
            Dealerships
          </h1>
          <p className="text-slate-400 text-sm mt-2">Manage your dealership locations and email routing</p>
        </div>
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogTrigger asChild>
            <Button
              onClick={openNew}
              className="bg-red-600 hover:bg-red-700 text-white font-bold text-xs tracking-widest uppercase hover:shadow-[0_0_15px_rgba(220,38,38,0.4)] transition-all"
              data-testid="add-dealership-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Dealership
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0A0A0A] border-[#262626] text-white max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl font-bold uppercase tracking-wider">
                {editing ? 'Edit Dealership' : 'Add New Dealership'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Dealership Name</Label>
                <Input
                  value={form.name}
                  onChange={e => setForm(p => ({ ...p, name: capitalizeWords(e.target.value) }))}
                  placeholder="Toyota Downtown"
                  className="mt-1.5 bg-[#111] border-[#333] h-11 text-sm placeholder:text-slate-600"
                  data-testid="dlr-name-input"
                  required
                />
              </div>

              {/* Gmail Processing Account */}
              <div className="bg-[#0F0F0F] border border-[#1A1A1A] rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-2 mb-1">
                  <Server className="w-3.5 h-3.5 text-red-500" />
                  <Label className="text-xs font-bold tracking-widest text-slate-400 uppercase">Email Configuration</Label>
                </div>
                <div>
                  <Label className="text-[10px] font-bold tracking-widest text-slate-500 uppercase">Processing Account</Label>
                  <Select
                    value={form.gmail_account_id || "none"}
                    onValueChange={v => setForm(p => ({ ...p, gmail_account_id: v === "none" ? "" : v }))}
                  >
                    <SelectTrigger className="mt-1.5 bg-[#111] border-[#333] h-11 text-sm" data-testid="dlr-gmail-account-select">
                      <SelectValue placeholder="Select processing account" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#171717] border-[#262626]">
                      <SelectItem value="none" className="text-slate-400 hover:bg-[#262626]">
                        None (no email processing)
                      </SelectItem>
                      {connectedAccounts.map(acct => (
                        <SelectItem key={acct.account_id} value={acct.account_id} className="text-white hover:bg-[#262626]">
                          <span className="font-mono text-xs">{acct.email}</span>
                          {acct.dealership_count > 0 && (
                            <span className="text-slate-500 ml-2">({acct.dealership_count} assigned)</span>
                          )}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {connectedAccounts.length === 0 && (
                    <p className="text-[10px] text-orange-400 mt-1">
                      No authorized accounts yet. Add one in Settings first.
                    </p>
                  )}
                </div>

                {/* VinSolutions: Multiple email aliases with websites — collapsible */}
                {form.crm === 'vinsolutions' ? (
                  <div className="border border-[#222] rounded-md overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setAliasesExpanded(p => !p)}
                      className="w-full flex items-center justify-between px-3 py-2.5 bg-[#0A0A0A] hover:bg-[#111] transition-colors"
                      data-testid="toggle-aliases-btn"
                    >
                      <Label className="text-[10px] font-bold tracking-widest text-slate-500 uppercase cursor-pointer">
                        Dealership Email Aliases
                        {(form.email_aliases || []).length > 0 && (
                          <span className="ml-2 text-slate-600">({(form.email_aliases || []).length})</span>
                        )}
                      </Label>
                      <ChevronDown className={`w-3.5 h-3.5 text-slate-500 transition-transform duration-200 ${aliasesExpanded ? 'rotate-180' : ''}`} />
                    </button>
                    {aliasesExpanded && (
                      <div className="p-3 pt-1 space-y-2.5 border-t border-[#222]">
                        <div className="flex justify-end">
                          <button
                            type="button"
                            onClick={() => setForm(p => ({
                              ...p,
                              email_aliases: [...(p.email_aliases || []), { alias: '', website: '' }]
                            }))}
                            className="text-[10px] text-red-400 hover:text-red-300 font-bold tracking-wider uppercase flex items-center gap-1 transition-colors"
                            data-testid="add-alias-btn"
                          >
                            <Plus className="w-3 h-3" /> Add Alias
                          </button>
                        </div>
                        {(form.email_aliases || []).length === 0 && (
                          <p className="text-[10px] text-slate-600 italic">No aliases added yet. Click &ldquo;Add Alias&rdquo; to add one.</p>
                        )}
                        {(form.email_aliases || []).map((entry, idx) => (
                          <div key={entry.alias || `alias-${idx}`} className="bg-[#0A0A0A] border border-[#222] rounded-md p-3 space-y-2" data-testid={`alias-entry-${idx}`}>
                            <div>
                              <Label className="text-[9px] font-bold tracking-widest text-slate-600 uppercase">Dealership Name</Label>
                              <Input
                                value={entry.dealership_name || ''}
                                onChange={e => {
                                  const updated = [...(form.email_aliases || [])];
                                  updated[idx] = { ...updated[idx], dealership_name: e.target.value };
                                  setForm(p => ({ ...p, email_aliases: updated }));
                                }}
                                placeholder="e.g. Paul Thigpen Ford Lincoln Vidalia"
                                className="mt-1 bg-[#111] border-[#333] h-9 text-sm placeholder:text-slate-600"
                                data-testid={`alias-dealership-name-${idx}`}
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="flex-1">
                                <Label className="text-[9px] font-bold tracking-widest text-slate-600 uppercase">Email Alias</Label>
                                <Input
                                  value={entry.alias || ''}
                                  onChange={e => {
                                    const updated = [...(form.email_aliases || [])];
                                    updated[idx] = { ...updated[idx], alias: e.target.value.toLowerCase().trim() };
                                    setForm(p => ({ ...p, email_aliases: updated }));
                                  }}
                                  placeholder="alias@etarsp.com"
                                  className="mt-1 bg-[#111] border-[#333] h-9 text-sm placeholder:text-slate-600 font-mono"
                                  data-testid={`alias-email-${idx}`}
                                />
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  const updated = (form.email_aliases || []).filter((_, i) => i !== idx);
                                  setForm(p => ({ ...p, email_aliases: updated }));
                                }}
                                className="mt-4 p-1.5 text-slate-600 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                                data-testid={`remove-alias-${idx}`}
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                            <div>
                              <Label className="text-[9px] font-bold tracking-widest text-slate-600 uppercase">Associated Website</Label>
                              <Input
                                value={entry.website || ''}
                                onChange={e => {
                                  const updated = [...(form.email_aliases || [])];
                                  updated[idx] = { ...updated[idx], website: formatWebsite(e.target.value) };
                                  setForm(p => ({ ...p, email_aliases: updated }));
                                }}
                                placeholder="dealerwebsite.com"
                                className="mt-1 bg-[#111] border-[#333] h-9 text-sm placeholder:text-slate-600"
                                data-testid={`alias-website-${idx}`}
                              />
                            </div>
                            <div>
                              <Label className="text-[9px] font-bold tracking-widest text-slate-600 uppercase">Pre-Owned Vehicles URL</Label>
                              <Input
                                value={entry.preowned_vehicles_url || ''}
                                onChange={e => {
                                  const updated = [...(form.email_aliases || [])];
                                  updated[idx] = { ...updated[idx], preowned_vehicles_url: e.target.value.trim() };
                                  setForm(p => ({ ...p, email_aliases: updated }));
                                }}
                                placeholder="https://dealerwebsite.com/used-inventory"
                                className="mt-1 bg-[#111] border-[#333] h-9 text-sm placeholder:text-slate-600"
                                data-testid={`alias-preowned-url-${idx}`}
                              />
                            </div>
                            <div>
                              <Label className="text-[9px] font-bold tracking-widest text-slate-600 uppercase">SMS Notification Phone</Label>
                              <Input
                                value={entry.notify_phone || ''}
                                onChange={e => {
                                  const updated = [...(form.email_aliases || [])];
                                  updated[idx] = { ...updated[idx], notify_phone: e.target.value };
                                  setForm(p => ({ ...p, email_aliases: updated }));
                                }}
                                placeholder="+1XXXXXXXXXX"
                                className="mt-1 bg-[#111] border-[#333] h-9 text-sm placeholder:text-slate-600"
                                data-testid={`alias-notify-phone-${idx}`}
                              />
                            </div>
                            <div>
                              <Label className="text-[9px] font-bold tracking-widest text-slate-600 uppercase">Email Notification Address</Label>
                              <Input
                                value={entry.notify_email || ''}
                                onChange={e => {
                                  const updated = [...(form.email_aliases || [])];
                                  updated[idx] = { ...updated[idx], notify_email: e.target.value.trim() };
                                  setForm(p => ({ ...p, email_aliases: updated }));
                                }}
                                placeholder="manager@dealership.com"
                                className="mt-1 bg-[#111] border-[#333] h-9 text-sm placeholder:text-slate-600"
                                data-testid={`alias-notify-email-${idx}`}
                              />
                            </div>
                            {/* Brand-specific websites: shown when alias has BOTH Ford and Lincoln */}
                            {(entry.brands || []).includes('Ford') && (entry.brands || []).includes('Lincoln') && (
                              <div className="mt-2 border border-[#262626] rounded-md p-3 space-y-3 bg-[#060606]">
                                <Label className="text-[9px] font-bold tracking-widest text-amber-500/80 uppercase">Brand-Specific Websites (Ford + Lincoln)</Label>
                                {['Ford', 'Lincoln'].map(brand => (
                                  <div key={brand} className="space-y-1.5">
                                    <Label className="text-[9px] font-bold tracking-widest text-slate-500 uppercase">{brand} Website</Label>
                                    <Input
                                      value={(entry.brand_websites || {})[brand]?.website || ''}
                                      onChange={e => {
                                        const updated = [...(form.email_aliases || [])];
                                        const bw = { ...(updated[idx].brand_websites || {}) };
                                        bw[brand] = { ...(bw[brand] || {}), website: formatWebsite(e.target.value) };
                                        updated[idx] = { ...updated[idx], brand_websites: bw };
                                        setForm(p => ({ ...p, email_aliases: updated }));
                                      }}
                                      placeholder={`${brand.toLowerCase()}dealersite.com`}
                                      className="bg-[#111] border-[#333] h-8 text-xs placeholder:text-slate-600"
                                      data-testid={`alias-brand-website-${brand.toLowerCase()}-${idx}`}
                                    />
                                    <Label className="text-[9px] font-bold tracking-widest text-slate-500 uppercase">{brand} Pre-Owned URL</Label>
                                    <Input
                                      value={(entry.brand_websites || {})[brand]?.preowned_vehicles_url || ''}
                                      onChange={e => {
                                        const updated = [...(form.email_aliases || [])];
                                        const bw = { ...(updated[idx].brand_websites || {}) };
                                        bw[brand] = { ...(bw[brand] || {}), preowned_vehicles_url: e.target.value.trim() };
                                        updated[idx] = { ...updated[idx], brand_websites: bw };
                                        setForm(p => ({ ...p, email_aliases: updated }));
                                      }}
                                      placeholder={`https://${brand.toLowerCase()}dealersite.com/used-inventory`}
                                      className="bg-[#111] border-[#333] h-8 text-xs placeholder:text-slate-600"
                                      data-testid={`alias-brand-preowned-${brand.toLowerCase()}-${idx}`}
                                    />
                                  </div>
                                ))}
                              </div>
                            )}
                            <div>
                              <Label className="text-[9px] font-bold tracking-widest text-slate-600 uppercase">Brands</Label>
                              <Select
                                value=""
                                onValueChange={v => {
                                  if (v && !(entry.brands || []).includes(v)) {
                                    const updated = [...(form.email_aliases || [])];
                                    updated[idx] = { ...updated[idx], brands: [...(updated[idx].brands || []), v].sort() };
                                    setForm(p => ({ ...p, email_aliases: updated }));
                                  }
                                }}
                              >
                                <SelectTrigger className="mt-1 bg-[#111] border-[#333] h-8 text-xs" data-testid={`alias-brands-select-${idx}`}>
                                  <SelectValue placeholder="Add brand..." />
                                </SelectTrigger>
                                <SelectContent className="bg-[#171717] border-[#262626]">
                                  {BRAND_OPTIONS.filter(b => !(entry.brands || []).includes(b)).map(brand => (
                                    <SelectItem key={brand} value={brand} className="text-white hover:bg-[#262626] text-xs">{brand}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              {(entry.brands || []).length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1.5">
                                  {(entry.brands || []).map(brand => (
                                    <span key={brand} className="inline-flex items-center gap-1 bg-[#1a1a1a] border border-[#333] rounded px-1.5 py-0.5 text-[10px] text-slate-300">
                                      {brand}
                                      <button type="button" onClick={() => {
                                        const updated = [...(form.email_aliases || [])];
                                        updated[idx] = { ...updated[idx], brands: (updated[idx].brands || []).filter(b => b !== brand) };
                                        setForm(p => ({ ...p, email_aliases: updated }));
                                      }} className="text-slate-500 hover:text-red-400"><X className="w-2.5 h-2.5" /></button>
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : form.crm === 'elead' ? (
                  /* eLead: Simple email aliases — User Name + Email Address only */
                  <div className="border border-[#222] rounded-md overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setAliasesExpanded(p => !p)}
                      className="w-full flex items-center justify-between px-3 py-2.5 bg-[#0A0A0A] hover:bg-[#111] transition-colors"
                      data-testid="toggle-elead-aliases-btn"
                    >
                      <Label className="text-[10px] font-bold tracking-widest text-slate-500 uppercase cursor-pointer">
                        Dealership Email Aliases
                        {(form.email_aliases || []).length > 0 && (
                          <span className="ml-2 text-slate-600">({(form.email_aliases || []).length})</span>
                        )}
                      </Label>
                      <ChevronDown className={`w-3.5 h-3.5 text-slate-500 transition-transform duration-200 ${aliasesExpanded ? 'rotate-180' : ''}`} />
                    </button>
                    {aliasesExpanded && (
                      <div className="p-3 pt-1 space-y-2.5 border-t border-[#222]">
                        <div className="flex justify-end">
                          <button
                            type="button"
                            onClick={() => {
                              if ((form.email_aliases || []).length >= 30) {
                                toast.error('Maximum 30 aliases per account');
                                return;
                              }
                              setForm(p => ({
                                ...p,
                                email_aliases: [...(p.email_aliases || []), { user_name: '', email_address: '' }]
                              }));
                            }}
                            className="text-[10px] text-red-400 hover:text-red-300 font-bold tracking-wider uppercase flex items-center gap-1 transition-colors"
                            data-testid="add-elead-alias-btn"
                          >
                            <Plus className="w-3 h-3" /> Add Alias
                          </button>
                        </div>
                        {(form.email_aliases || []).length === 0 && (
                          <p className="text-[10px] text-slate-600 italic">No aliases added yet. Click &ldquo;Add Alias&rdquo; to add one.</p>
                        )}
                        {(form.email_aliases || []).map((entry, idx) => (
                          <div key={`elead-alias-${idx}`} className="bg-[#0A0A0A] border border-[#222] rounded-md p-3 space-y-2" data-testid={`elead-alias-entry-${idx}`}>
                            <div className="flex items-center gap-2">
                              <div className="flex-1">
                                <Label className="text-[9px] font-bold tracking-widest text-slate-600 uppercase">User Name</Label>
                                <Input
                                  value={entry.user_name || ''}
                                  onChange={e => {
                                    const updated = [...(form.email_aliases || [])];
                                    updated[idx] = { ...updated[idx], user_name: e.target.value };
                                    setForm(p => ({ ...p, email_aliases: updated }));
                                  }}
                                  placeholder="e.g. John Smith"
                                  className="mt-1 bg-[#111] border-[#333] h-9 text-sm placeholder:text-slate-600"
                                  data-testid={`elead-alias-username-${idx}`}
                                />
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  const updated = (form.email_aliases || []).filter((_, i) => i !== idx);
                                  setForm(p => ({ ...p, email_aliases: updated }));
                                }}
                                className="mt-4 p-1.5 text-slate-600 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                                data-testid={`remove-elead-alias-${idx}`}
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                            <div>
                              <Label className="text-[9px] font-bold tracking-widest text-slate-600 uppercase">Email Address</Label>
                              <Input
                                value={entry.email_address || ''}
                                onChange={e => {
                                  const updated = [...(form.email_aliases || [])];
                                  updated[idx] = { ...updated[idx], email_address: e.target.value.toLowerCase().trim() };
                                  setForm(p => ({ ...p, email_aliases: updated }));
                                }}
                                placeholder="alias@etarsp.com"
                                className="mt-1 bg-[#111] border-[#333] h-9 text-sm placeholder:text-slate-600 font-mono"
                                data-testid={`elead-alias-email-${idx}`}
                              />
                            </div>
                            <div>
                              <Label className="text-[9px] font-bold tracking-widest text-slate-600 uppercase">SMS Notification Phone</Label>
                              <Input
                                value={entry.phone_number || ''}
                                onChange={e => {
                                  const updated = [...(form.email_aliases || [])];
                                  updated[idx] = { ...updated[idx], phone_number: e.target.value };
                                  setForm(p => ({ ...p, email_aliases: updated }));
                                }}
                                placeholder="e.g. 480-555-1234"
                                className="mt-1 bg-[#111] border-[#333] h-9 text-sm placeholder:text-slate-600"
                                data-testid={`elead-alias-phone-${idx}`}
                              />
                            </div>
                            <div>
                              <Label className="text-[9px] font-bold tracking-widest text-slate-600 uppercase">Email Notification Address</Label>
                              <Input
                                value={entry.notify_email || ''}
                                onChange={e => {
                                  const updated = [...(form.email_aliases || [])];
                                  updated[idx] = { ...updated[idx], notify_email: e.target.value.toLowerCase().trim() };
                                  setForm(p => ({ ...p, email_aliases: updated }));
                                }}
                                placeholder="e.g. manager@dealership.com"
                                className="mt-1 bg-[#111] border-[#333] h-9 text-sm placeholder:text-slate-600 font-mono"
                                data-testid={`elead-alias-notify-email-${idx}`}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  /* Non-VinSolutions/Non-eLead: single email alias */
                  <div>
                    <Label className="text-[10px] font-bold tracking-widest text-slate-500 uppercase">Dealership Email Alias</Label>
                    <Input
                      value={form.email_alias}
                      onChange={e => setForm(p => ({ ...p, email_alias: e.target.value.toLowerCase().trim() }))}
                      placeholder="billcramergm@etarsp.com"
                      className="mt-1.5 bg-[#111] border-[#333] h-11 text-sm placeholder:text-slate-600 font-mono"
                      data-testid="dlr-email-alias-input"
                    />
                  </div>
                )}
              </div>

              <div className="border border-[#222] rounded-md overflow-hidden">
                <button
                  type="button"
                  onClick={() => setUrlsExpanded(p => !p)}
                  className="w-full flex items-center justify-between px-3 py-2.5 bg-[#0A0A0A] hover:bg-[#111] transition-colors"
                  data-testid="toggle-urls-btn"
                >
                  <Label className="text-[10px] font-bold tracking-widest text-slate-500 uppercase cursor-pointer">
                    Dealership URL's
                  </Label>
                  <ChevronDown className={`w-3.5 h-3.5 text-slate-500 transition-transform duration-200 ${urlsExpanded ? 'rotate-180' : ''}`} />
                </button>
                {urlsExpanded && (
                  <div className="p-3 pt-1 space-y-3 border-t border-[#222]">
              {form.crm !== 'vinsolutions' && form.brands && form.brands.length > 0 && (
                <div>
                  <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase mb-2 block">Brand-Specific Websites</Label>
                  <div className="space-y-2">
                    {form.brands.map(brand => (
                      <div key={brand} className="flex items-center gap-3">
                        <span className="text-xs text-slate-300 font-bold w-24 shrink-0">{brand}</span>
                        <Input
                          value={(form.brand_websites || {})[brand] || ''}
                          onChange={e => setForm(p => ({
                            ...p,
                            brand_websites: { ...(p.brand_websites || {}), [brand]: formatWebsite(e.target.value) }
                          }))}
                          placeholder={form.website || 'brand-specific-site.com'}
                          className="bg-[#111] border-[#333] h-9 text-sm placeholder:text-slate-600"
                          data-testid={`dlr-brand-website-${brand.toLowerCase()}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {form.crm !== 'vinsolutions' && (
              <div>
                <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Reply-To Email</Label>
                <Input
                  type="email"
                  value={form.reply_to_email}
                  onChange={e => setForm(p => ({ ...p, reply_to_email: e.target.value.trim() }))}
                  placeholder="sales@dealership.com"
                  className="mt-1.5 bg-[#111] border-[#333] h-11 text-sm placeholder:text-slate-600"
                  data-testid="dlr-reply-to-email-input"
                />
              </div>
              )}
              <div>
                <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Test Drive URL</Label>
                <Input
                  value={form.test_drive_url}
                  onChange={e => setForm(p => ({ ...p, test_drive_url: e.target.value.trim() }))}
                  placeholder="https://www.example.com/testdrive.aspx (leave blank for default)"
                  className="mt-1.5 bg-[#111] border-[#333] h-11 text-sm placeholder:text-slate-600"
                  data-testid="dlr-test-drive-url-input"
                />
              </div>
              <div>
                <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Pre-Owned Vehicles URL</Label>
                <Input
                  value={form.preowned_vehicles_url}
                  onChange={e => setForm(p => ({ ...p, preowned_vehicles_url: e.target.value.trim() }))}
                  placeholder="https://example.com/inventory/used (leave blank for default)"
                  className="mt-1.5 bg-[#111] border-[#333] h-11 text-sm placeholder:text-slate-600"
                  data-testid="dlr-preowned-url-input"
                />
              </div>
              <div>
                <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">New Vehicles URL</Label>
                <Input
                  value={form.new_vehicles_url}
                  onChange={e => setForm(p => ({ ...p, new_vehicles_url: e.target.value.trim() }))}
                  placeholder="https://example.com/new-inventory/index.htm"
                  className="mt-1.5 bg-[#111] border-[#333] h-11 text-sm placeholder:text-slate-600"
                  data-testid="dlr-new-vehicles-url-input"
                />
              </div>
              <div>
                <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Pre-Owned Specials URL</Label>
                <Input
                  value={form.preowned_specials_url}
                  onChange={e => setForm(p => ({ ...p, preowned_specials_url: e.target.value.trim() }))}
                  placeholder="https://example.com/specials/pre-owned/"
                  className="mt-1.5 bg-[#111] border-[#333] h-11 text-sm placeholder:text-slate-600"
                  data-testid="dlr-preowned-specials-url-input"
                />
              </div>
              <div>
                <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">New Vehicle Specials URL</Label>
                <Input
                  value={form.new_vehicle_specials_url}
                  onChange={e => setForm(p => ({ ...p, new_vehicle_specials_url: e.target.value.trim() }))}
                  placeholder="https://example.com/specials/new/"
                  className="mt-1.5 bg-[#111] border-[#333] h-11 text-sm placeholder:text-slate-600"
                  data-testid="dlr-new-vehicle-specials-url-input"
                />
              </div>
              <div>
                <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Window Sticker URL Template</Label>
                <Input
                  value={form.window_sticker_url_template}
                  onChange={e => setForm(p => ({ ...p, window_sticker_url_template: e.target.value.trim() }))}
                  placeholder="https://example.com/api/legacy/pse/windowsticker/kia?vin={vin}&accountId=xxx&isCPO=false"
                  className="mt-1.5 bg-[#111] border-[#333] h-11 text-sm placeholder:text-slate-600"
                  data-testid="dlr-window-sticker-template-input"
                />
                <p className="text-[11px] text-slate-600 mt-1">Use <code className="text-amber-500/70">{'{vin}'}</code> as placeholder. System swaps in VIN automatically.</p>
              </div>
              <div>
                <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Dealership Hours URL</Label>
                <Input
                  value={form.hours_url}
                  onChange={e => setForm(p => ({ ...p, hours_url: e.target.value.trim() }))}
                  placeholder="https://www.example.com/hours"
                  className="mt-1.5 bg-[#111] border-[#333] h-11 text-sm placeholder:text-slate-600"
                  data-testid="dlr-hours-url-input"
                />
              </div>
              <div>
                <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Finance Application URL</Label>
                <Input
                  value={form.finance_application_url}
                  onChange={e => setForm(p => ({ ...p, finance_application_url: e.target.value.trim() }))}
                  placeholder="https://www.example.com/finance-application"
                  className="mt-1.5 bg-[#111] border-[#333] h-11 text-sm placeholder:text-slate-600"
                  data-testid="dlr-finance-url-input"
                />
              </div>
              <div>
                <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Service Department URL</Label>
                <Input
                  value={form.service_url}
                  onChange={e => setForm(p => ({ ...p, service_url: e.target.value.trim() }))}
                  placeholder="https://www.example.com/service"
                  className="mt-1.5 bg-[#111] border-[#333] h-11 text-sm placeholder:text-slate-600"
                  data-testid="dlr-service-url-input"
                />
              </div>
              <div>
                <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Service Hours</Label>
                <Input
                  value={form.service_hours}
                  onChange={e => setForm(p => ({ ...p, service_hours: e.target.value.trim() }))}
                  placeholder="Mon-Fri 7:30AM-6:00PM, Sat 8:00AM-4:00PM"
                  className="mt-1.5 bg-[#111] border-[#333] h-11 text-sm placeholder:text-slate-600"
                  data-testid="dlr-service-hours-input"
                />
              </div>
              {/* Warranty PDFs Section */}
              <div className="col-span-2">
                <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase mb-2 block">Warranty PDFs (Per Brand)</Label>
                <WarrantyPdfManager dealershipId={editing} brands={form.brands || []} />
              </div>
                  </div>
                )}
              </div>
              <div className="border border-[#222] rounded-md overflow-hidden">
                <button
                  type="button"
                  onClick={() => setDealerInfoExpanded(p => !p)}
                  className="w-full flex items-center justify-between px-3 py-2.5 bg-[#0A0A0A] hover:bg-[#111] transition-colors"
                  data-testid="toggle-dealer-info-btn"
                >
                  <Label className="text-[10px] font-bold tracking-widest text-slate-500 uppercase cursor-pointer">
                    Dealership Information
                  </Label>
                  <ChevronDown className={`w-3.5 h-3.5 text-slate-500 transition-transform duration-200 ${dealerInfoExpanded ? 'rotate-180' : ''}`} />
                </button>
                {dealerInfoExpanded && (
                  <div className="p-3 pt-1 space-y-3 border-t border-[#222]">
              <div>
                <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Address</Label>
                <Input
                  value={form.address}
                  onChange={e => setForm(p => ({ ...p, address: capitalizeWords(e.target.value) }))}
                  placeholder="123 Main Street"
                  className="mt-1.5 bg-[#111] border-[#333] h-11 text-sm placeholder:text-slate-600"
                  data-testid="dlr-address-input"
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">City</Label>
                  <Input
                    value={form.city}
                    onChange={e => setForm(p => ({ ...p, city: capitalizeWords(e.target.value) }))}
                    placeholder="Dallas"
                    className="mt-1.5 bg-[#111] border-[#333] h-11 text-sm placeholder:text-slate-600"
                    data-testid="dlr-city-input"
                  />
                </div>
                <div>
                  <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">State</Label>
                  <Select value={form.state} onValueChange={v => setForm(p => ({ ...p, state: v }))}>
                    <SelectTrigger className="mt-1.5 bg-[#111] border-[#333] h-11 text-sm" data-testid="dlr-state-select">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#171717] border-[#262626] max-h-60">
                      {US_STATES.map(state => (
                        <SelectItem key={state.value} value={state.value} className="text-white hover:bg-[#262626]">
                          {state.value} - {state.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Zip Code</Label>
                  <Input
                    value={form.zip_code}
                    onChange={e => {
                      const v = e.target.value.replace(/\D/g, '').slice(0, 5);
                      setForm(p => ({ ...p, zip_code: v }));
                    }}
                    placeholder="75001"
                    maxLength={5}
                    className="mt-1.5 bg-[#111] border-[#333] h-11 text-sm placeholder:text-slate-600"
                    data-testid="dlr-zip-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Phone</Label>
                  <Input
                    value={form.phone}
                    onChange={e => setForm(p => ({ ...p, phone: formatPhone(e.target.value) }))}
                    placeholder="555-123-4567"
                    className="mt-1.5 bg-[#111] border-[#333] h-11 text-sm placeholder:text-slate-600"
                    data-testid="dlr-phone-input"
                  />
                </div>
                <div>
                  <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Website</Label>
                  <Input
                    value={form.website}
                    onChange={e => setForm(p => ({ ...p, website: formatWebsite(e.target.value) }))}
                    placeholder="toyotadowntown.com"
                    className="mt-1.5 bg-[#111] border-[#333] h-11 text-sm placeholder:text-slate-600"
                    data-testid="dlr-website-input"
                  />
                </div>
              </div>
                  </div>
                )}
              </div>
              <div>
                <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">CRM System</Label>
                <Select value={form.crm} onValueChange={v => setForm(p => ({ ...p, crm: v }))}>
                  <SelectTrigger className="mt-1.5 bg-[#111] border-[#333] h-11 text-sm" data-testid="dlr-crm-select">
                    <SelectValue placeholder="Select CRM" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#171717] border-[#262626]">
                    {CRM_OPTIONS.map(crm => (
                      <SelectItem key={crm.value} value={crm.value} className="text-white hover:bg-[#262626]">
                        {crm.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {/* Brands */}
              <div>
                <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Brands</Label>
                <Select
                  value=""
                  onValueChange={v => {
                    if (v && !form.brands.includes(v)) {
                      setForm(p => ({ ...p, brands: [...p.brands, v].sort() }));
                    }
                  }}
                >
                  <SelectTrigger className="mt-1.5 bg-[#111] border-[#333] h-11 text-sm" data-testid="dlr-brands-select">
                    <SelectValue placeholder="Select brands to add" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#171717] border-[#262626] max-h-60">
                    {BRAND_OPTIONS.filter(b => !(form.brands || []).includes(b)).map(brand => (
                      <SelectItem key={brand} value={brand} className="text-white hover:bg-[#262626]">
                        {brand}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {(form.brands || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2" data-testid="dlr-brands-list">
                    {(form.brands || []).map(brand => (
                      <span key={brand} className="inline-flex items-center gap-1 bg-red-500/10 text-red-400 border border-red-500/20 rounded-md px-2.5 py-1 text-xs font-medium">
                        {brand}
                        <button
                          type="button"
                          onClick={() => setForm(p => ({ ...p, brands: p.brands.filter(b => b !== brand) }))}
                          className="hover:text-white transition-colors"
                          data-testid={`remove-brand-${brand}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <Button
                type="submit"
                disabled={saving}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-bold text-xs tracking-widest uppercase h-11"
                data-testid="dlr-submit-btn"
              >
                {saving ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  editing ? 'Update Dealership' : 'Add Dealership'
                )}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Gmail Settings Dialog */}
      <Dialog open={!!showSettings} onOpenChange={() => setShowSettings(null)}>
        <DialogContent className="bg-[#0A0A0A] border-[#262626] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase tracking-wider flex items-center gap-2">
              <Settings className="w-5 h-5 text-red-500" /> Email Settings
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 mt-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-medium text-white">Auto-Respond</Label>
                <p className="text-xs text-slate-500 mt-0.5">Automatically send responses without review</p>
              </div>
              <Switch
                checked={gmailSettings.auto_respond}
                onCheckedChange={(checked) => setGmailSettings(p => ({ ...p, auto_respond: checked }))}
                data-testid="auto-respond-switch"
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-medium text-white">Email Polling</Label>
                <p className="text-xs text-slate-500 mt-0.5">Enable automatic email checking</p>
              </div>
              <Switch
                checked={gmailSettings.polling_enabled}
                onCheckedChange={(checked) => setGmailSettings(p => ({ ...p, polling_enabled: checked }))}
                data-testid="polling-enabled-switch"
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-medium text-white">SMS Notifications</Label>
                <p className="text-xs text-slate-500 mt-0.5">Send text message to customer when lead has a phone number</p>
              </div>
              <Switch
                checked={gmailSettings.sms_enabled}
                onCheckedChange={(checked) => setGmailSettings(p => ({ ...p, sms_enabled: checked }))}
                data-testid="sms-enabled-switch"
              />
            </div>
            {gmailSettings.sms_enabled && (
            <div>
              <Label className="text-sm font-medium text-white">Dealership Twilio Phone Number</Label>
              <p className="text-xs text-slate-500 mt-0.5">Dedicated phone number for this dealership. Leave blank to use the shared number.</p>
              <Input
                value={gmailSettings.twilio_phone_number}
                onChange={(e) => setGmailSettings(p => ({ ...p, twilio_phone_number: e.target.value }))}
                placeholder="+1XXXXXXXXXX"
                className="mt-1.5 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-600 text-sm"
                data-testid="twilio-phone-number-input"
              />
            </div>
            )}
            {dealerships.find(d => d.dealership_id === showSettings)?.crm === 'vinsolutions' && (
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-medium text-white">Respond to Bad Leads</Label>
                <p className="text-xs text-slate-500 mt-0.5">Send responses even when VinSolutions marks the lead as Bad Lead</p>
              </div>
              <Switch
                checked={gmailSettings.respond_to_bad_leads}
                onCheckedChange={(checked) => setGmailSettings(p => ({ ...p, respond_to_bad_leads: checked }))}
                data-testid="respond-bad-leads-switch"
              />
            </div>
            )}
            <div className="border-t border-slate-700/50 pt-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium text-white">Lead Notifications</Label>
                  <p className="text-xs text-slate-500 mt-0.5">SMS alert to staff when a new lead is received</p>
                </div>
                <Switch
                  checked={gmailSettings.lead_notify_enabled}
                  onCheckedChange={(checked) => setGmailSettings(p => ({ ...p, lead_notify_enabled: checked }))}
                  data-testid="lead-notify-switch"
                />
              </div>
              {gmailSettings.lead_notify_enabled && (
                <div className="mt-2 space-y-2">
                  <Input
                    placeholder="Notification phone (e.g., 480-555-1234)"
                    value={gmailSettings.lead_notify_phone}
                    onChange={e => setGmailSettings(p => ({ ...p, lead_notify_phone: e.target.value }))}
                    className="bg-slate-800/50 border-slate-700 text-white text-sm h-9"
                    data-testid="lead-notify-phone-input"
                  />
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs text-slate-400">SMS Format:</span>
                    <div className="flex rounded-md overflow-hidden border border-slate-700">
                      <button
                        type="button"
                        onClick={() => setGmailSettings(p => ({ ...p, lead_notify_format: 'formatted' }))}
                        className={`px-3 py-1 text-xs font-medium transition-colors ${
                          gmailSettings.lead_notify_format === 'formatted'
                            ? 'bg-red-600 text-white'
                            : 'bg-slate-800/50 text-slate-400 hover:text-white'
                        }`}
                        data-testid="notify-format-formatted"
                      >
                        Formatted
                      </button>
                      <button
                        type="button"
                        onClick={() => setGmailSettings(p => ({ ...p, lead_notify_format: 'raw' }))}
                        className={`px-3 py-1 text-xs font-medium transition-colors ${
                          gmailSettings.lead_notify_format === 'raw'
                            ? 'bg-red-600 text-white'
                            : 'bg-slate-800/50 text-slate-400 hover:text-white'
                        }`}
                        data-testid="notify-format-raw"
                      >
                        Raw Email
                      </button>
                    </div>
                  </div>
                  <p className="text-[10px] text-slate-500">
                    {gmailSettings.lead_notify_format === 'raw'
                      ? 'Sends the original CRM email body as-is'
                      : 'Sends a clean summary (name, vehicle, phone, email)'}
                  </p>
                </div>
              )}
            </div>
            <div className="border-t border-slate-700/50 pt-3">
              <Label className="text-sm font-medium text-white">UTM Campaign</Label>
              <p className="text-xs text-slate-500 mt-0.5">Appends UTM tracking to all outbound links (leave empty to disable)</p>
              <Input
                placeholder="e.g., ccad_lead_response"
                value={gmailSettings.utm_campaign}
                onChange={e => setGmailSettings(p => ({ ...p, utm_campaign: e.target.value }))}
                className="mt-2 bg-slate-800/50 border-slate-700 text-white text-sm h-9"
                data-testid="utm-campaign-input"
              />
              {gmailSettings.utm_campaign && (
                <div className="mt-2 p-2 bg-slate-800/30 rounded text-[10px] text-slate-400 font-mono space-y-0.5">
                  <div>utm_source=eta</div>
                  <div>utm_medium=email</div>
                  <div>utm_campaign={gmailSettings.utm_campaign}</div>
                  <div>utm_content=<span className="text-slate-500">cta_vdp | cta_test_drive | cta_create_deal | cta_primary</span></div>
                </div>
              )}
            </div>
            <div className="border-t border-slate-700/50 pt-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium text-white">Appointment Scheduling</Label>
                  <p className="text-xs text-slate-500 mt-0.5">Send test drive or dealer visit scheduling to customers after CRM response</p>
                </div>
                <Switch
                  checked={gmailSettings.scheduling_enabled}
                  onCheckedChange={v => setGmailSettings(p => ({ ...p, scheduling_enabled: v }))}
                  data-testid="scheduling-enabled-toggle"
                />
              </div>
              {gmailSettings.scheduling_enabled && (
                <div className="mt-2 space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400">Preferred Channel:</span>
                    <div className="flex rounded-md overflow-hidden border border-slate-700">
                      <button
                        type="button"
                        onClick={() => setGmailSettings(p => ({ ...p, scheduling_preferred_channel: 'email' }))}
                        className={`px-3 py-1 text-xs font-medium transition-colors ${
                          gmailSettings.scheduling_preferred_channel === 'email'
                            ? 'bg-red-600 text-white'
                            : 'bg-slate-800/50 text-slate-400 hover:text-white'
                        }`}
                        data-testid="scheduling-channel-email"
                      >
                        Email
                      </button>
                      <button
                        type="button"
                        onClick={() => setGmailSettings(p => ({ ...p, scheduling_preferred_channel: 'sms' }))}
                        className={`px-3 py-1 text-xs font-medium transition-colors ${
                          gmailSettings.scheduling_preferred_channel === 'sms'
                            ? 'bg-red-600 text-white'
                            : 'bg-slate-800/50 text-slate-400 hover:text-white'
                        }`}
                        data-testid="scheduling-channel-sms"
                      >
                        SMS
                      </button>
                    </div>
                  </div>
                  <p className="text-[10px] text-slate-500">
                    {gmailSettings.scheduling_preferred_channel === 'sms'
                      ? 'SMS preferred — falls back to email if no phone number available'
                      : 'Email preferred — falls back to SMS if no email available'}
                  </p>
                </div>
              )}
            </div>
            <Button
              onClick={() => saveSettings(showSettings)}
              className="w-full bg-red-600 hover:bg-red-700 text-white font-bold text-xs tracking-widest uppercase h-10"
              data-testid="save-settings-btn"
            >
              Save Settings
            </Button>
            <div className="border-t border-slate-700/50 pt-3">
              <Label className="text-sm font-medium text-white">Dealer Portal Invite</Label>
              <p className="text-xs text-slate-500 mt-0.5">Generate a registration link for dealership staff</p>
              <div className="flex gap-2 mt-2">
                <Input
                  placeholder="staff@dealership.com"
                  id="invite-email-input"
                  className="bg-slate-800/50 border-slate-700 text-white text-xs h-8 flex-1"
                  data-testid="invite-email-input"
                />
                <Button
                  size="sm"
                  className="bg-slate-700 hover:bg-slate-600 text-white text-xs h-8 px-3"
                  data-testid="generate-invite-btn"
                  onClick={async () => {
                    const emailInput = document.getElementById('invite-email-input');
                    const inviteEmail = emailInput?.value?.trim();
                    if (!inviteEmail) { toast.error('Enter an email address'); return; }
                    try {
                      const { data } = await axios.post(`${API}/portal/invites`, {
                        dealership_id: showSettings,
                        email: inviteEmail,
                      }, { headers, withCredentials: true });
                      toast.success('Invite created!');
                      navigator.clipboard.writeText(data.invite_url);
                      toast.success('Link copied to clipboard');
                      emailInput.value = '';
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Failed to create invite');
                    }
                  }}
                >
                  Generate & Copy Link
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : dealerships.length === 0 ? (
        <Card className="bg-[#0A0A0A] border-[#262626] p-12 text-center">
          <Building2 className="w-12 h-12 mx-auto mb-4 text-slate-600" />
          <h3 className="font-heading text-xl font-semibold text-white uppercase mb-2">No Dealerships Yet</h3>
          <p className="text-slate-500 text-sm mb-6">Add your first dealership to start receiving and managing leads.</p>
          <Button
            onClick={openNew}
            className="bg-red-600 hover:bg-red-700 text-white font-bold text-xs tracking-widest uppercase"
            data-testid="add-first-dealership-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Add Your First Dealership
          </Button>
        </Card>
      ) : (
        <div className="columns-1 lg:columns-2 gap-6 space-y-6" data-testid="dealerships-grid">
          {sortedDealerships.map((dlr, i) => {
            const account = getAccountForDealership(dlr);
            const isConnected = account?.is_connected || dlr.gmail_connected;
            const isExpanded = expandedCards[dlr.dealership_id] ?? false;
            const toggleExpand = () => setExpandedCards(prev => ({ ...prev, [dlr.dealership_id]: !prev[dlr.dealership_id] }));

            return (
              <Card
                key={dlr.dealership_id}
                className="bg-[#0A0A0A] border-[#262626] hover:border-[#404040] transition-all duration-300 break-inside-avoid"
                data-testid={`dealership-card-${dlr.dealership_id}`}
              >
                <div className="p-5 cursor-pointer select-none" onClick={toggleExpand} data-testid={`toggle-card-${dlr.dealership_id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-red-600/10 flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-red-500" strokeWidth={1.5} />
                      </div>
                      <div>
                        <h3 className="font-heading text-lg font-semibold text-white uppercase tracking-wider">{dlr.name}</h3>
                        {dlr.brands && dlr.brands.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {dlr.brands.map(brand => (
                              <Badge key={brand} className="bg-slate-500/10 text-slate-400 border border-slate-500/20 text-[8px] font-bold tracking-wider uppercase">
                                {brand}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                      <button
                        onClick={(e) => { e.stopPropagation(); handleEdit(dlr); }}
                        className="p-2 text-slate-500 hover:text-white hover:bg-white/5 rounded-md transition-colors"
                        data-testid={`edit-dlr-${dlr.dealership_id}`}
                        title="Edit dealership"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeactivate(dlr.dealership_id, dlr.name); }}
                        className="p-2 text-slate-500 hover:text-amber-400 hover:bg-amber-500/10 rounded-md transition-colors"
                        data-testid={`deactivate-dlr-${dlr.dealership_id}`}
                        title="Deactivate dealership"
                      >
                        <PowerOff className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDelete(dlr.dealership_id); }}
                        className="p-2 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded-md transition-colors"
                        data-testid={`delete-dlr-${dlr.dealership_id}`}
                        title="Delete dealership"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>

                {isExpanded && (<>
                <div className="p-5 space-y-2.5 text-sm border-t border-[#262626]">
                  {dlr.crm === 'vinsolutions' && dlr.email_aliases && dlr.email_aliases.length > 0 ? (
                    <div className="space-y-1.5">
                      {dlr.email_aliases.map((entry, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-slate-400">
                          <Mail className="w-4 h-4 text-slate-500 flex-shrink-0" strokeWidth={1.5} />
                          <span className="font-mono text-xs text-slate-300">{entry.alias}</span>
                          {entry.website && (
                            <span className="text-[10px] text-slate-500">({entry.website})</span>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : dlr.crm === 'elead' && dlr.email_aliases && dlr.email_aliases.length > 0 ? (
                    <div className="space-y-1.5">
                      {dlr.email_aliases.map((entry, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-slate-400">
                          <Mail className="w-4 h-4 text-slate-500 flex-shrink-0" strokeWidth={1.5} />
                          <span className="text-xs text-slate-300">{entry.user_name}</span>
                          {entry.email_address && (
                            <span className="font-mono text-[10px] text-slate-500">{entry.email_address}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : dlr.email_alias && (
                    <div className="flex items-center gap-2 text-slate-400">
                      <Mail className="w-4 h-4 text-slate-500 flex-shrink-0" strokeWidth={1.5} />
                      <span className="font-mono text-xs text-slate-300">{dlr.email_alias}</span>
                    </div>
                  )}
                  {dlr.phone && (
                    <div className="flex items-center gap-2 text-slate-400">
                      <Phone className="w-4 h-4 text-slate-500 flex-shrink-0" strokeWidth={1.5} />
                      <span>{dlr.phone}</span>
                    </div>
                  )}
                  {dlr.website && (
                    <div className="flex items-center gap-2 text-slate-400">
                      <Globe className="w-4 h-4 text-slate-500 flex-shrink-0" strokeWidth={1.5} />
                      <span>{dlr.website}</span>
                    </div>
                  )}
                  {dlr.address && (
                    <div className="flex items-center gap-2 text-slate-400">
                      <MapPin className="w-4 h-4 text-slate-500 flex-shrink-0" strokeWidth={1.5} />
                      <span>{dlr.address}</span>
                    </div>
                  )}
                </div>

                {/* Business Hours Section */}
                <div className="px-5 pb-4 pt-1 border-b border-[#262626]">
                  <div className="flex items-center justify-between mb-1.5">
                    <button
                      onClick={() => setCollapsedBizHours(p => { const next = { ...p, [dlr.dealership_id]: !(p[dlr.dealership_id] ?? true) }; localStorage.setItem('collapsedBizHours', JSON.stringify(next)); return next; })}
                      className="text-xs font-bold tracking-widest text-slate-500 uppercase flex items-center gap-1.5 hover:text-white transition-colors"
                      data-testid={`toggle-biz-hours-${dlr.dealership_id}`}
                    >
                      {(collapsedBizHours[dlr.dealership_id] ?? true) ? <ChevronRight className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                      <Clock className="w-3 h-3" /> Business Hours
                    </button>
                    {!(collapsedBizHours[dlr.dealership_id] ?? true) && (
                    <button
                      onClick={() => editingBusinessHours === dlr.dealership_id ? setEditingBusinessHours(null) : initBusinessHours(dlr)}
                      className="text-[10px] text-slate-500 hover:text-white transition-colors flex items-center gap-1"
                      data-testid={`edit-hours-${dlr.dealership_id}`}
                    >
                      <Edit2 className="w-3 h-3" /> {editingBusinessHours === dlr.dealership_id ? 'Close' : 'Edit'}
                    </button>
                    )}
                  </div>
                  {!(collapsedBizHours[dlr.dealership_id] ?? true) && (<>
                  {editingBusinessHours === dlr.dealership_id ? (
                    <div className="space-y-2">
                      {DAYS.map(day => (
                        <div key={day} className="flex items-center gap-2" data-testid={`biz-hours-${day}-${dlr.dealership_id}`}>
                          <div className="w-8 text-[10px] font-bold text-slate-400 uppercase">{DAY_LABELS[day]}</div>
                          <Switch
                            checked={businessHoursData[day]?.enabled ?? true}
                            onCheckedChange={v => setBusinessHoursData(p => ({ ...p, [day]: { ...p[day], enabled: v } }))}
                            className="scale-75"
                            data-testid={`biz-toggle-${day}`}
                          />
                          {businessHoursData[day]?.enabled ? (
                            <>
                              <Select
                                value={businessHoursData[day]?.all_day ? '24hr' : 'custom'}
                                onValueChange={v => setBusinessHoursData(p => ({ ...p, [day]: { ...p[day], all_day: v === '24hr' } }))}
                              >
                                <SelectTrigger className="h-7 w-20 bg-[#111] border-[#333] text-[10px]" data-testid={`biz-type-${day}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="24hr">24 Hr</SelectItem>
                                  <SelectItem value="custom">Custom</SelectItem>
                                </SelectContent>
                              </Select>
                              {!businessHoursData[day]?.all_day && (
                                <>
                                  <Select
                                    value={businessHoursData[day]?.start || '9:00'}
                                    onValueChange={v => setBusinessHoursData(p => ({ ...p, [day]: { ...p[day], start: v } }))}
                                  >
                                    <SelectTrigger className="h-7 w-[88px] bg-[#111] border-[#333] text-[10px]" data-testid={`biz-start-${day}`}>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {HOURS.map(h => <SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>)}
                                    </SelectContent>
                                  </Select>
                                  <span className="text-[10px] text-slate-500">to</span>
                                  <Select
                                    value={businessHoursData[day]?.end || '19:00'}
                                    onValueChange={v => setBusinessHoursData(p => ({ ...p, [day]: { ...p[day], end: v } }))}
                                  >
                                    <SelectTrigger className="h-7 w-[88px] bg-[#111] border-[#333] text-[10px]" data-testid={`biz-end-${day}`}>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {HOURS.map(h => <SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>)}
                                    </SelectContent>
                                  </Select>
                                </>
                              )}
                            </>
                          ) : (
                            <span className="text-[10px] text-slate-600 italic">Closed</span>
                          )}
                        </div>
                      ))}
                      <div className="flex gap-2 pt-1">
                        <Button
                          size="sm"
                          onClick={() => saveBusinessHoursStructured(dlr.dealership_id)}
                          className="bg-red-600 hover:bg-red-700 text-white text-[10px] font-bold tracking-wider uppercase h-7 px-3"
                          data-testid={`save-hours-${dlr.dealership_id}`}
                        >
                          Save
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditingBusinessHours(null)}
                          className="border-[#333] bg-[#111] hover:bg-[#1a1a1a] text-white text-[10px] font-bold tracking-wider uppercase h-7 px-3"
                          data-testid={`cancel-hours-${dlr.dealership_id}`}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (dlr.business_hours || dlr.business_hours_structured) ? (
                    <div className="text-xs text-slate-300 leading-relaxed space-y-0.5" data-testid={`hours-display-${dlr.dealership_id}`}>
                      {dlr.business_hours_structured ? DAYS.map(day => {
                        const d = (dlr.business_hours_structured || {})[day];
                        if (!d) return null;
                        const label = { sunday: 'Sunday', monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday', thursday: 'Thursday', friday: 'Friday', saturday: 'Saturday' }[day];
                        if (!d.enabled) return <div key={day} className="flex gap-2"><span className="w-24 text-slate-500 font-medium">{label}</span><span className="text-slate-600">Closed</span></div>;
                        if (d.all_day) return <div key={day} className="flex gap-2"><span className="w-24 text-slate-500 font-medium">{label}</span><span>24 Hours</span></div>;
                        const fmt = (t) => { const [hr, mn] = (t || '9:00').split(':'); const h12 = parseInt(hr) % 12 || 12; const ampm = parseInt(hr) < 12 ? 'AM' : 'PM'; return mn === '00' ? `${h12}${ampm}` : `${h12}:${mn}${ampm}`; };
                        return <div key={day} className="flex gap-2"><span className="w-24 text-slate-500 font-medium">{label}</span><span>{fmt(d.start)} - {fmt(d.end)}</span></div>;
                      }) : <p>{dlr.business_hours}</p>}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-600 italic" data-testid={`no-hours-${dlr.dealership_id}`}>
                      Not set — click Edit to set business hours
                    </p>
                  )}
                  </>)}
                </div>

                {/* Coverage Hours Section */}
                <div className="px-5 pb-4 pt-1 border-b border-[#262626]">
                  <div className="flex items-center justify-between mb-1.5">
                    <button
                      onClick={() => setCollapsedCovHours(p => { const next = { ...p, [dlr.dealership_id]: !(p[dlr.dealership_id] ?? true) }; localStorage.setItem('collapsedCovHours', JSON.stringify(next)); return next; })}
                      className="text-xs font-bold tracking-widest text-slate-500 uppercase flex items-center gap-1.5 hover:text-white transition-colors"
                      data-testid={`toggle-cov-hours-${dlr.dealership_id}`}
                    >
                      {(collapsedCovHours[dlr.dealership_id] ?? true) ? <ChevronRight className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                      <Clock className="w-3 h-3" /> Coverage Hours
                    </button>
                    {!(collapsedCovHours[dlr.dealership_id] ?? true) && (
                    <button
                      onClick={() => editingCoverage === dlr.dealership_id ? setEditingCoverage(null) : initCoverageHours(dlr)}
                      className="text-[10px] text-slate-500 hover:text-white transition-colors flex items-center gap-1"
                      data-testid={`edit-coverage-${dlr.dealership_id}`}
                    >
                      <Edit2 className="w-3 h-3" /> {editingCoverage === dlr.dealership_id ? 'Close' : 'Edit'}
                    </button>
                    )}
                  </div>
                  {!collapsedCovHours[dlr.dealership_id] && (<>
                  {editingCoverage === dlr.dealership_id ? (
                    <div className="space-y-2">
                      {DAYS.map(day => (
                        <div key={day} className="flex items-center gap-2" data-testid={`coverage-${day}-${dlr.dealership_id}`}>
                          <div className="w-8 text-[10px] font-bold text-slate-400 uppercase">{DAY_LABELS[day]}</div>
                          <Switch
                            checked={coverageHours[day]?.enabled ?? true}
                            onCheckedChange={v => setCoverageHours(p => ({ ...p, [day]: { ...p[day], enabled: v } }))}
                            className="scale-75"
                            data-testid={`coverage-toggle-${day}`}
                          />
                          {coverageHours[day]?.enabled ? (
                            <>
                              <Select
                                value={coverageHours[day]?.all_day ? '24hr' : 'custom'}
                                onValueChange={v => setCoverageHours(p => ({ ...p, [day]: { ...p[day], all_day: v === '24hr' } }))}
                              >
                                <SelectTrigger className="h-7 w-20 bg-[#111] border-[#333] text-[10px]" data-testid={`coverage-type-${day}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="24hr">24 Hr</SelectItem>
                                  <SelectItem value="custom">Custom</SelectItem>
                                </SelectContent>
                              </Select>
                              {!coverageHours[day]?.all_day && (
                                <>
                                  <Select
                                    value={coverageHours[day]?.start || '8:00'}
                                    onValueChange={v => setCoverageHours(p => ({ ...p, [day]: { ...p[day], start: v } }))}
                                  >
                                    <SelectTrigger className="h-7 w-[88px] bg-[#111] border-[#333] text-[10px]" data-testid={`coverage-start-${day}`}>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {HOURS.map(h => <SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>)}
                                    </SelectContent>
                                  </Select>
                                  <span className="text-[10px] text-slate-500">to</span>
                                  <Select
                                    value={coverageHours[day]?.end || '20:00'}
                                    onValueChange={v => setCoverageHours(p => ({ ...p, [day]: { ...p[day], end: v } }))}
                                  >
                                    <SelectTrigger className="h-7 w-[88px] bg-[#111] border-[#333] text-[10px]" data-testid={`coverage-end-${day}`}>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {HOURS.map(h => <SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>)}
                                    </SelectContent>
                                  </Select>
                                </>
                              )}
                            </>
                          ) : (
                            <span className="text-[10px] text-slate-600 italic">Closed</span>
                          )}
                        </div>
                      ))}
                      <div className="flex gap-2 pt-1">
                        <Button
                          size="sm"
                          onClick={() => saveCoverageHours(dlr.dealership_id)}
                          className="bg-red-600 hover:bg-red-700 text-white text-[10px] font-bold tracking-wider uppercase h-7 px-3"
                          data-testid={`save-coverage-${dlr.dealership_id}`}
                        >
                          Save
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditingCoverage(null)}
                          className="border-[#333] bg-[#111] hover:bg-[#1a1a1a] text-white text-[10px] font-bold tracking-wider uppercase h-7 px-3"
                          data-testid={`cancel-coverage-${dlr.dealership_id}`}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : dlr.coverage_hours ? (
                    <div className="text-xs text-slate-300 leading-relaxed" data-testid={`coverage-display-${dlr.dealership_id}`}>
                      {DAYS.map(day => {
                        const d = (dlr.coverage_hours || {})[day];
                        if (!d || !d.enabled) return <div key={day}><span className="text-slate-500 w-8 inline-block">{DAY_LABELS[day]}</span> <span className="text-slate-600">Closed</span></div>;
                        if (d.all_day) return <div key={day}><span className="text-slate-500 w-8 inline-block">{DAY_LABELS[day]}</span> 24 Hours</div>;
                        const fmt = t => { const h = parseInt(t); return `${h % 12 || 12}:00 ${h < 12 ? 'AM' : 'PM'}`; };
                        return <div key={day}><span className="text-slate-500 w-8 inline-block">{DAY_LABELS[day]}</span> {fmt(d.start)} - {fmt(d.end)}</div>;
                      })}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-600 italic" data-testid={`no-coverage-${dlr.dealership_id}`}>
                      Not set — click Edit to configure
                    </p>
                  )}
                  </>)}
                </div>

                {/* Email Integration Section */}
                <div className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-bold tracking-widest text-slate-500 uppercase">Email Integration</span>
                    <div className="flex items-center gap-1.5">
                      {account?.watch_active && (() => {
                        const exp = account.watch_expiration;
                        const isExpired = exp ? new Date() > new Date(typeof exp === 'string' && /^\d+$/.test(exp) ? parseInt(exp) : exp) : false;
                        return !isExpired ? (
                          <Badge className="text-[9px] font-bold tracking-wider uppercase border bg-emerald-500/10 text-emerald-400 border-emerald-500/20 gap-1" data-testid={`push-status-${dlr.dealership_id}`}>
                            <Wifi className="w-2.5 h-2.5" /> Push
                          </Badge>
                        ) : (
                          <Badge className="text-[9px] font-bold tracking-wider uppercase border bg-amber-500/10 text-amber-400 border-amber-500/20 gap-1" data-testid={`push-status-${dlr.dealership_id}`}>
                            <Wifi className="w-2.5 h-2.5" /> Expired
                          </Badge>
                        );
                      })()}
                      <Badge className={`text-[9px] font-bold tracking-wider uppercase border ${
                        isConnected
                          ? 'bg-green-500/10 text-green-400 border-green-500/20'
                          : 'bg-slate-500/10 text-slate-400 border-slate-500/20'
                      }`} data-testid={`email-status-${dlr.dealership_id}`}>
                        {isConnected ? 'Connected' : 'Not Connected'}
                      </Badge>
                    </div>
                  </div>

                  {account ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-sm text-slate-300 bg-[#111] rounded-md px-3 py-2">
                        <Server className="w-4 h-4 text-green-400 flex-shrink-0" />
                        <div className="min-w-0">
                          <span className="font-mono text-xs block truncate">{account.email}</span>
                          {!account.is_connected && (
                            <span className="text-[10px] text-yellow-400">Pending authorization in Settings</span>
                          )}
                        </div>
                      </div>
                      {account.is_connected && (
                        <div className="grid grid-cols-2 gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => pollEmails(dlr.dealership_id)}
                            disabled={pollingDealership === dlr.dealership_id}
                            className="border-[#333] bg-[#111] hover:bg-[#1a1a1a] text-white text-[10px] font-bold tracking-wider uppercase h-9"
                            data-testid={`poll-emails-${dlr.dealership_id}`}
                          >
                            {pollingDealership === dlr.dealership_id ? (
                              <RefreshCw className="w-3 h-3 animate-spin" />
                            ) : (
                              <><RefreshCw className="w-3 h-3 mr-1" /> Poll</>
                            )}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openSettings(dlr)}
                            className="border-[#333] bg-[#111] hover:bg-[#1a1a1a] text-white text-[10px] font-bold tracking-wider uppercase h-9"
                            data-testid={`settings-${dlr.dealership_id}`}
                          >
                            <Settings className="w-3 h-3 mr-1" /> Settings
                          </Button>
                        </div>
                      )}
                      {dlr.auto_respond && (
                        <div className="flex items-center gap-2 text-xs text-green-400 bg-green-500/5 rounded-md px-3 py-2">
                          <Zap className="w-3 h-3" />
                          <span>Auto-respond is enabled</span>
                        </div>
                      )}
                      {account?.watch_active && account?.last_push_at && (
                        <div className="flex items-center gap-2 text-xs text-emerald-400 bg-emerald-500/5 rounded-md px-3 py-2" data-testid={`last-push-${dlr.dealership_id}`}>
                          <Wifi className="w-3 h-3" />
                          <span>Push active — last received {(() => {
                            const d = new Date(account.last_push_at);
                            const diff = Math.floor((Date.now() - d.getTime()) / 1000);
                            if (diff < 60) return 'just now';
                            if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
                            if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
                            return `${Math.floor(diff / 86400)}d ago`;
                          })()}</span>
                        </div>
                      )}
                    </div>
                  ) : dlr.gmail_connected ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-sm text-slate-300 bg-[#111] rounded-md px-3 py-2">
                        <Mail className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                        <div className="min-w-0">
                          <span className="font-mono text-xs block truncate">{dlr.gmail_email || 'Legacy connection'}</span>
                          <span className="text-[10px] text-yellow-400">Legacy setup - assign a processing account for better scalability</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => pollEmails(dlr.dealership_id)}
                          disabled={pollingDealership === dlr.dealership_id}
                          className="border-[#333] bg-[#111] hover:bg-[#1a1a1a] text-white text-[10px] font-bold tracking-wider uppercase h-9"
                          data-testid={`poll-emails-${dlr.dealership_id}`}
                        >
                          {pollingDealership === dlr.dealership_id ? (
                            <RefreshCw className="w-3 h-3 animate-spin" />
                          ) : (
                            <><RefreshCw className="w-3 h-3 mr-1" /> Poll</>
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openSettings(dlr)}
                          className="border-[#333] bg-[#111] hover:bg-[#1a1a1a] text-white text-[10px] font-bold tracking-wider uppercase h-9"
                          data-testid={`settings-${dlr.dealership_id}`}
                        >
                          <Settings className="w-3 h-3 mr-1" /> Settings
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-[#0F0F0F] border border-[#1A1A1A] rounded-lg p-3">
                      <p className="text-xs text-slate-500">
                        No processing account assigned. Edit this dealership to select one, or{' '}
                        <button
                          onClick={() => window.location.href = '/settings'}
                          className="text-red-400 hover:text-red-300 underline"
                          data-testid={`go-to-settings-${dlr.dealership_id}`}
                        >
                          add an account in Settings
                        </button>.
                      </p>
                    </div>
                  )}
                </div>
                </>)}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
